/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthProfileSerializer } from "@shared/modules/auth/serializer-types";
import { TicketBasketType } from "@shared/modules/basket/types";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import * as cacheStorage from "@shared/utils/storage/cache-storage";
import { getQueryParameters } from "@shared/utils/urls/url-utils";
import sumBy from "lodash/sumBy";
import moment from "moment";

import {
  AnalyticEventOptions,
  AnalyticEventTypes,
  BaseAnalyticsType,
  FacebookPixelInstance,
} from "./types/analytics-types";

const FBQ_IS_FB_EVENT_KEY = "fbq_is_fb_event";
const FBQ_IS_FB_REFERENCE_KEY = "fbq_is_fb_reference";

type ItemListType = {
  id: number;
  quantity: number;
  item_price: string;
};

/**
 * Build item list to send to facebook
 * @param basket
 *
 */
const buildItemList = (basket: TicketBasketType): ItemListType[] => {
  const contents = [];
  basket.item_groups?.forEach((ig: TicketItemGroupType) => {
    contents.push({
      id: ig.type || ig.product_attribute,
      quantity: ig.quantity,
      item_price: ig.price,
    });
  });
  return contents;
};

const isFbqLoaded = () => {
  return (
    typeof window !== "undefined" && window && typeof window?.fbq === "function"
  );
};

/**
 * Wrapper for window.fbq to insert metadata about Official Events Ad (OEA) campaigns
 */
const $fbq = (event: string, params: any, opts?: AnalyticEventOptions) => {
  const clonedParams = { ...params };
  // track eventref if OEA and add 'fb_oea' if referral from fb
  const isFBEvent = cacheStorage.get(FBQ_IS_FB_EVENT_KEY);
  if (isFBEvent) {
    const isFBReference = cacheStorage.get(FBQ_IS_FB_REFERENCE_KEY);
    clonedParams.eventref = isFBReference ? "fb_oea" : "";
  }

  const fbOpts = {
    eventID: opts?.uuid,
  };

  window?.fbq?.("track", event, clonedParams, fbOpts);
};

type InitWithUserData = BaseAnalyticsType["init"] extends (
  id: infer U
) => infer R
  ? (id: U, userData?: null | AuthProfileSerializer) => R
  : never;
export interface FBPixelAnalyticsType extends BaseAnalyticsType {
  init: InitWithUserData;
  viewContent: AnalyticEventTypes["viewContent"];
}

const facebookpixel: FBPixelAnalyticsType = {
  init(id, userData, ...args) {
    if (!isFbqLoaded()) return;

    // If id is alreaday initialized, return and skip initialization
    const availableConfig = window._fbq?.instance?.pixelsByID[id];
    if (availableConfig && availableConfig.userData.em === userData?.email)
      return;

    const amParams: FacebookPixelInstance<typeof id>["userData"] = {};
    if (userData) {
      amParams.em = `${userData.email}`.toLowerCase(); // All lowercase to let pixel hash it themselves
      amParams.external_id = `${userData.id}`; // External ID
      amParams.fn = userData.first_name;
      amParams.ln = userData.last_name;
      amParams.ph = userData.phone_number;
    }

    // Tell fbq to add advanced matching parameters on to the pixel instance configuration
    window?.fbq?.("init", id, amParams, ...args);

    // Re-enable consent when initializing
    window?.fbq?.("consent", "grant");
  },

  /**
   * Used to create Custom Audiences or Custom conversions that match a URL.
   * If you want to track a key page (eg. event detail page), use `viewContent`
   * @see https://stackoverflow.com/questions/39978989/facebook-pixel-pageview-vs-viewcontent
   */
  pageView(opts?: AnalyticEventOptions) {
    if (!isFbqLoaded()) return;

    // Use fbq directly, as we don't want to include OEA data
    window?.fbq?.("track", "PageView", null, {
      eventID: opts?.uuid,
    });
  },

  /**
   * Track a key page.
   */
  viewContent(item, opts?: AnalyticEventOptions) {
    if (!isFbqLoaded()) return;

    // Indicate if event is Facebook event
    const eventref = getQueryParameters().eventRef;
    const cacheStorageTimeout = moment().add(1, "hours");
    cacheStorage.set(
      FBQ_IS_FB_EVENT_KEY,
      "facebook_official_events_id" in item &&
        !!item.facebook_official_events_id,
      cacheStorageTimeout
    );
    cacheStorage.set(FBQ_IS_FB_REFERENCE_KEY, !!eventref, cacheStorageTimeout);

    $fbq(
      "ViewContent",
      {
        content_ids: [item.id],
        content_type: item.type ?? "product_group",
        content_name: item.name,
      },
      opts
    );
  },

  addToCart(group, count, venue, opts) {
    if (!isFbqLoaded()) return;

    const subtotal = Number(group.subtotal);
    const serviceFees = Number(group.service_fees);
    const taxes = Number(group.taxes);

    $fbq(
      "AddToCart",
      {
        content_ids: [
          group.event || group.membership_group || group.product_attribute,
        ],
        content_type: "product_group",
        content_name: group.description,
        value: (subtotal + serviceFees + taxes).toFixed(2),
        contents: [
          {
            id: group.type || group.membership_level || group.product_attribute,
            quantity: count,
            item_price: group.price,
          },
        ],
        currency: venue.currency,
      },
      opts
    );
  },

  beginCheckout(basket) {
    if (!isFbqLoaded()) return;

    const contentIDs = [
      ...new Set(
        basket.item_groups?.map(
          (ig: TicketItemGroupType) => ig.event || ig.product
        )
      ),
    ];
    const numItems = sumBy(
      basket.item_groups,
      (ig: TicketItemGroupType) => ig.quantity
    );
    const contents = buildItemList(basket);
    $fbq("InitiateCheckout", {
      content_ids: contentIDs,
      content_type: "product_group",
      currency: basket.currency,
      num_items: numItems,
      contents,
      value: Number(basket.total_price).toFixed(2),
    });
  },

  postPurchase(basket, _, opts) {
    if (!isFbqLoaded()) return;

    const contentIDs = [
      ...new Set(
        basket.item_groups?.map(
          (ig: TicketItemGroupType) => ig.event || ig.product
        )
      ),
    ];
    const numItems = sumBy(
      basket.item_groups,
      (ig: TicketItemGroupType) => ig.quantity
    );
    const contents = buildItemList(basket);

    $fbq(
      "Purchase",
      {
        content_ids: contentIDs,
        content_type: "product_group",
        contents,
        num_items: numItems,
        value: basket.total_price,
        currency: basket.currency,
      },
      opts
    );
  },

  grantConsent() {
    if (!isFbqLoaded()) return;

    window?.fbq?.("consent", "grant");
  },

  revokeConsent() {
    if (!isFbqLoaded()) return;

    window?.fbq?.("consent", "revoke");
  },

  track(opts, event: string, params: any) {
    if (!isFbqLoaded()) return;

    $fbq(event, params, opts);
  },
};

export default facebookpixel;
